import { createFileRoute } from "@tanstack/react-router";
import { aabb2AddPoint, aabb2Grow } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { offsetPolyLine2 } from "geom/offset";
import type { DCEL } from "geom/polyclip/dcel";
import {
  offsetClosedPolyLine2,
  buildOffsetDCELAndPolygon,
  dcelComputeWindingNumbers,
} from "geom/polyclip/offsetClosedPolyLine2";
import { renderDCEL, renderDCELEdges, renderDCELVerts } from "geom/polyclip/render";
import { OffsetClosedPolyLine2TestCases } from "geom/polyclip/test/offsetClosedPolyLine2TestCases";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { useEffect, useState, useCallback } from "react";
import Canvas from "./-Canvas";

const initialPolylineKey = 0;
const initialOffsetDistance = 10;

function drawPolyLine(
  ctx: CanvasRenderingContext2D,
  polyline: PolyLine2,
  strokeStyle: string = "blue",
  lineWidth: number = 1,
) {
  if (!polyline || polyline.length < 4) {
    return;
  }
  ctx.beginPath();
  ctx.moveTo(polyline[0], polyline[1]);
  for (let i = 2; i < polyline.length; i += 2) {
    ctx.lineTo(polyline[i], polyline[i + 1]);
  }
  if (
    polyline.length > 1 &&
    (polyline[0] !== polyline[polyline.length - 2] || polyline[1] !== polyline[polyline.length - 1])
  ) {
    ctx.lineTo(polyline[0], polyline[1]);
  }
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dirX: number,
  dirY: number,
  length: number,
  strokeStyle: string = "purple",
  lineWidth: number = 1,
): void {
  ctx.save();
  ctx.beginPath();
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = lineWidth;
  const tipX = x + dirX * length;
  const tipY = y + dirY * length;
  ctx.moveTo(x, y);
  ctx.lineTo(tipX, tipY);
  const arrowAngle = Math.PI / 6;
  const headLength = length * 0.4;
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(
    tipX - headLength * Math.cos(Math.atan2(dirY, dirX) - arrowAngle),
    tipY - headLength * Math.sin(Math.atan2(dirY, dirX) - arrowAngle),
  );
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(
    tipX - headLength * Math.cos(Math.atan2(dirY, dirX) + arrowAngle),
    tipY - headLength * Math.sin(Math.atan2(dirY, dirX) + arrowAngle),
  );
  ctx.stroke();
  ctx.restore();
}

function OffsetTestPage() {
  const [selectedPolylineKey, setSelectedPolylineKey] = useState<number>(initialPolylineKey);
  const [offsetDistance, setOffsetDistance] = useState<number>(initialOffsetDistance);
  const [variableOffset, setVariableOffset] = useState<boolean>(false);
  const [offsetSegmentArray, setOffsetSegmentArray] = useState<number[][]>([]);
  const [offsetResultPolylines, setOffsetResultPolylines] = useState<PolyLine2[][]>([]);
  const [simpleOffsetResultPolyline, setSimpleOffsetResultPolyline] = useState<PolyLine2[] | null>(null);
  const [showSimpleOffsetPolyline, setShowSimpleOffsetPolyline] = useState<boolean>(false);
  const [showFinalOffsetPolylines, setShowFinalOffsetPolylines] = useState<boolean>(true);
  const [sourcePolyline, setSourcePolyline] = useState<PolyLine2[]>(
    OffsetClosedPolyLine2TestCases[initialPolylineKey].polylines,
  );
  const [dcel, setDCEL] = useState<DCEL | null>(null);
  const [showDcel, setShowDcel] = useState<boolean>(false);
  const [showRangeCurves, setShowRangeCurves] = useState<boolean>(false);

  useEffect(() => {
    setSourcePolyline(OffsetClosedPolyLine2TestCases[selectedPolylineKey].polylines);
  }, [selectedPolylineKey]);

  useEffect(() => {
    // When the selected polyline changes, reset the offsetSegmentArray to match the new structure
    const newArray = sourcePolyline.map((poly) => {
      const segCount = (poly.length - 2) / 2;
      return Array(segCount).fill(offsetDistance);
    });
    setOffsetSegmentArray(newArray);
  }, [selectedPolylineKey, sourcePolyline, offsetDistance]);

  // Ensure offsetSegmentArray is always the correct shape for the UI
  const safeOffsetSegmentArray = sourcePolyline.map((poly, i) => {
    const segCount = (poly.length - 2) / 2;
    if (!offsetSegmentArray[i] || offsetSegmentArray[i].length !== segCount) {
      return Array(segCount).fill(offsetDistance);
    }
    return offsetSegmentArray[i].map((v) => v ?? offsetDistance);
  });

  const handleOffsetSegmentChange = (polyIdx: number, segIdx: number, value: number) => {
    setOffsetSegmentArray((arr) => {
      const newArr = arr.map((inner) => [...inner]);
      if (!newArr[polyIdx]) {
        const segCount = (sourcePolyline[polyIdx].length - 2) / 2;
        newArr[polyIdx] = Array(segCount).fill(offsetDistance);
      }
      newArr[polyIdx][segIdx] = value;
      return newArr;
    });
  };

  useEffect(() => {
    if (sourcePolyline) {
      let offsetValue: number | number[][];
      if (variableOffset) {
        offsetValue = safeOffsetSegmentArray;
      } else {
        offsetValue = offsetDistance;
      }

      const { dcel, rawOffsetPolygons } = buildOffsetDCELAndPolygon(sourcePolyline, offsetValue);
      setDCEL(dcel);

      console.time("offsetClosedPolyLine2");
      const finalPolylines = offsetClosedPolyLine2(sourcePolyline, offsetValue);
      console.timeEnd("offsetClosedPolyLine2");

      let offsetPolyValue: number;
      if (Array.isArray(offsetValue)) {
        if (Array.isArray(offsetValue[0])) {
          offsetPolyValue = (offsetValue as number[][])[0][0];
        } else {
          offsetPolyValue = offsetValue[0] as number;
        }
      } else {
        offsetPolyValue = offsetDistance;
      }

      console.time("offsetPolyLine2");
      offsetPolyLine2(sourcePolyline[0], offsetPolyValue);
      console.timeEnd("offsetPolyLine2");
      setOffsetResultPolylines(finalPolylines);

      if (rawOffsetPolygons && rawOffsetPolygons.length > 0) {
        setSimpleOffsetResultPolyline(rawOffsetPolygons.map((x) => polyLine2New([...x.flat(), x[0][0], x[0][1]])));
      } else {
        setSimpleOffsetResultPolyline(null);
      }
    }
  }, [sourcePolyline, offsetDistance, offsetSegmentArray, variableOffset]);

  const renderMainVisualization = useCallback(
    (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, viewScale: number) => {
      const extendAABBWithPolyline = (aabb: AABB2, poly: PolyLine2 | null) => {
        if (!poly) {
          return;
        }
        for (let i = 0; i < poly.length; i += 2) {
          aabb2AddPoint(aabb, poly[i], poly[i + 1]);
        }
      };

      const aabb = {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
      };

      for (const polyline of sourcePolyline) {
        extendAABBWithPolyline(aabb, polyline);
      }

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const PADDING = 100;
      aabb2Grow(aabb, PADDING, PADDING);

      const { minX, minY, maxX, maxY } = aabb;
      const width = maxX - minX;
      const height = maxY - minY;

      const scale = Math.min(canvas.width / width, canvas.height / height);

      const ratio = window.devicePixelRatio;
      const ss = scale / ratio;
      ctx.translate(-ss * minX, canvas.height / ratio + ss * minY);
      ctx.scale(ss, -ss);

      if (sourcePolyline) {
        for (const polyline of sourcePolyline) {
          drawPolyLine(ctx, polyline, "rgba(13, 13, 64, 0.5)");
        }
      }

      if (showFinalOffsetPolylines && offsetResultPolylines && offsetResultPolylines.length > 0) {
        offsetResultPolylines.forEach((pline) => {
          for (const loop of pline) {
            drawPolyLine(ctx, loop, "green");
          }
        });
      }

      if (showSimpleOffsetPolyline && simpleOffsetResultPolyline && simpleOffsetResultPolyline.length) {
        for (const polyline of simpleOffsetResultPolyline) {
          drawPolyLine(ctx, polyline, "red");

          const poly = polyline;
          const n = poly.length;
          const arrowLength = 8;
          for (let i = 0; i < n - 2; i += 2) {
            const p1x = poly[i];
            const p1y = poly[i + 1];
            const p2x = poly[i + 2];
            const p2y = poly[i + 3];
            const midX = (p1x + p2x) / 2;
            const midY = (p1y + p2y) / 2;
            let tangentDx = p2x - p1x;
            let tangentDy = p2y - p1y;
            const len = Math.sqrt(tangentDx * tangentDx + tangentDy * tangentDy);

            if (len > 1e-4) {
              tangentDx /= len;
              tangentDy /= len;
              drawArrow(ctx, midX, midY, tangentDx, tangentDy, arrowLength, "purple", 1);
            }
          }
        }
      }

      if (showRangeCurves && sourcePolyline) {
        // Draw range curves from -50 to 50 in steps of 5
        for (let d = -50; d <= 50; d += 5) {
          const rangePolylines = offsetClosedPolyLine2(sourcePolyline, d);
          for (const pline of rangePolylines) {
            for (const loop of pline) {
              drawPolyLine(ctx, loop, "rgba(128, 128, 128, 0.3)", 0.5);
            }
          }
        }
      }

      if (showDcel && dcel) {
        const windingNumbers = dcelComputeWindingNumbers(dcel);

        renderDCEL(ctx, dcel);
        renderDCELEdges(
          ctx,
          dcel,
          2 / viewScale,
          undefined,
          undefined,
          (halfEdge) => windingNumbers[dcel.halfEdgeToFace[halfEdge]],
        );
        renderDCELVerts(ctx, dcel, 2 / viewScale);
      }

      ctx.restore();
    },
    [
      dcel,
      showDcel,
      sourcePolyline,
      offsetResultPolylines,
      showFinalOffsetPolylines,
      simpleOffsetResultPolyline,
      showSimpleOffsetPolyline,
      showRangeCurves,
    ],
  );

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="polyline-select">Select Polyline: </label>
        <select
          id="polyline-select"
          value={selectedPolylineKey}
          onChange={(e) => setSelectedPolylineKey(parseInt(e.target.value))}
          style={{ marginRight: "20px" }}
        >
          {Object.keys(OffsetClosedPolyLine2TestCases).map((key) => (
            <option key={key} value={key}>
              {OffsetClosedPolyLine2TestCases[key].name.charAt(0).toUpperCase() +
                OffsetClosedPolyLine2TestCases[key].name.slice(1)}
            </option>
          ))}
        </select>

        <label htmlFor="variable-offset">Variable Offset (per segment): </label>
        <input
          type="checkbox"
          id="variable-offset"
          checked={variableOffset}
          onChange={(e) => setVariableOffset(e.target.checked)}
          style={{ marginRight: "20px", marginLeft: "10px" }}
        />

        {!variableOffset && (
          <>
            <label htmlFor="offset-distance">Offset Distance: </label>
            <input
              type="number"
              id="offset-distance"
              value={offsetDistance}
              onChange={(e) => setOffsetDistance(parseFloat(e.target.value))}
              style={{ width: "60px", marginRight: "10px" }}
            />
            <input
              type="range"
              min="-50"
              max="50"
              step="1"
              value={offsetDistance}
              onChange={(e) => setOffsetDistance(parseFloat(e.target.value))}
              style={{ width: "200px", verticalAlign: "middle" }}
            />
            <span style={{ marginLeft: "10px" }}>{offsetDistance.toFixed(1)}</span>
          </>
        )}
        {variableOffset && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 8 }}>
            {sourcePolyline.map((poly, polyIdx) => (
              <div key={polyIdx} style={{ border: "1px solid #ccc", padding: 8, borderRadius: 4, marginBottom: 8 }}>
                <div style={{ marginBottom: 4, fontWeight: "bold" }}>
                  Polyline {polyIdx + 1} (Segments: {(poly.length - 2) / 2})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {Array.from({ length: (poly.length - 2) / 2 }).map((_, segIdx) => (
                    <div key={segIdx} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <label>Segment {segIdx + 1}:</label>
                      <input
                        type="number"
                        value={safeOffsetSegmentArray[polyIdx][segIdx]}
                        onChange={(e) => handleOffsetSegmentChange(polyIdx, segIdx, parseFloat(e.target.value))}
                        style={{ width: "60px" }}
                      />
                      <input
                        type="range"
                        min="-50"
                        max="50"
                        step="1"
                        value={safeOffsetSegmentArray[polyIdx][segIdx]}
                        onChange={(e) => handleOffsetSegmentChange(polyIdx, segIdx, parseFloat(e.target.value))}
                        style={{ width: "200px", verticalAlign: "middle" }}
                      />
                      <span>{safeOffsetSegmentArray[polyIdx][segIdx].toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="show-simple-offset">Show Simple Offset Polyline: </label>
        <input
          type="checkbox"
          id="show-simple-offset"
          checked={showSimpleOffsetPolyline}
          onChange={(e) => setShowSimpleOffsetPolyline(e.target.checked)}
        />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="show-final-offset">Show Final Offset Polylines: </label>
        <input
          type="checkbox"
          id="show-final-offset"
          checked={showFinalOffsetPolylines}
          onChange={(e) => setShowFinalOffsetPolylines(e.target.checked)}
        />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="show-dcel">Show DCEL: </label>
        <input type="checkbox" id="show-dcel" checked={showDcel} onChange={(e) => setShowDcel(e.target.checked)} />
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label htmlFor="show-range-curves">Show Range Curves: </label>
        <input
          type="checkbox"
          id="show-range-curves"
          checked={showRangeCurves}
          onChange={(e) => setShowRangeCurves(e.target.checked)}
        />
      </div>

      <Canvas canvasWidth={800} canvasHeight={600} render={renderMainVisualization} />
    </div>
  );
}

export const Route = createFileRoute("/debug/polyclip/offset-closed-test")({
  component: OffsetTestPage,
});
